/* eslint-disable max-len */
/**
 * variant-picker-style-availability.js
 *
 * Extends VariantPicker to handle cross-Style-Number Color availability.
 *
 * Hierarchy: Style Number (option 1) > Color (option 2) > Size (option 3)
 *
 * Rules:
 *   1. A Color is considered available if ANY Style Number has an available
 *      variant with that Color — never mark a Color unavailable just because
 *      the currently selected Style Number doesn't carry it.
 *
 *   2. When a Color is selected that the current Style Number doesn't have,
 *      automatically switch the Style Number to the first one that does have
 *      an available variant with that Color.
 *
 * What this file does NOT do:
 *   - It does not touch Style Number availability (the base theme handles that).
 *   - It does not touch Size availability.
 *
 * Dependencies:
 *   - variant-picker.js must be loaded first (defines VariantPicker)
 *   - variant-picker.liquid must include inside <variant-picker>:
 *       <script type="application/json" data-variant-picker-all-variants>
 *         {{ product.variants | json }}
 *       </script>
 *
 * Usage:
 *   In variant-picker.liquid, after the existing variant-picker.js script tag:
 *   <script src="{{ 'variant-picker-style-availability.js' | asset_url }}" defer="defer"></script>
 */

(function () {

  // ─── Helpers ────────────────────────────────────────────────────────────────

  /**
   * Get all variants from the JSON block injected by the Liquid snippet.
   * @param {HTMLElement} pickerEl
   * @returns {Array}
   */
  function getAllVariants(pickerEl) {
    const section = pickerEl.section || pickerEl.closest('.shopify-section');
    if (!section) return [];
    const dataEl = section.querySelector('[data-variant-picker-all-variants]');
    if (!dataEl) return [];
    try {
      return JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      console.warn('[style-avail] Could not parse variants JSON:', e);
      return [];
    }
  }

  /**
   * Get the currently selected value for an option by index (0-based).
   * Handles both radio (listed) and dropdown (custom-select) selector types.
   * @param {HTMLElement} pickerEl
   * @param {number}      index0
   * @returns {string|null}
   */
  function getSelectedValueAt(pickerEl, index0) {
    const selector = pickerEl.optionSelectors && pickerEl.optionSelectors[index0];
    if (!selector) return null;

    if (selector.dataset.selectorType === 'dropdown') {
      const sel = selector.querySelector('.custom-select__option[aria-selected="true"]');
      return sel && sel.dataset.value !== '' ? sel.dataset.value : null;
    }

    const checked = selector.querySelector('input:checked');
    return checked ? checked.value : null;
  }

  /**
   * Get all Color option elements (index 1) as { el, value } pairs.
   * @param {HTMLElement} pickerEl
   * @returns {Array<{el: HTMLElement, value: string}>}
   */
  function getColorOptions(pickerEl) {
    const selector = pickerEl.optionSelectors && pickerEl.optionSelectors[1];
    if (!selector) return [];

    if (selector.dataset.selectorType === 'dropdown') {
      return Array.from(
        selector.querySelectorAll('.custom-select__option[data-value]:not([data-value=""])')
      ).map((el) => ({ el, value: el.dataset.value }));
    }

    return Array.from(
      selector.querySelectorAll('input.js-option')
    ).map((el) => ({ el, value: el.value }));
  }

  /**
   * Get all Style Number option elements (index 0) as { el, value } pairs.
   * @param {HTMLElement} pickerEl
   * @returns {Array<{el: HTMLElement, value: string}>}
   */
  function getStyleNumberOptions(pickerEl) {
    const selector = pickerEl.optionSelectors && pickerEl.optionSelectors[0];
    if (!selector) return [];

    if (selector.dataset.selectorType === 'dropdown') {
      return Array.from(
        selector.querySelectorAll('.custom-select__option[data-value]:not([data-value=""])')
      ).map((el) => ({ el, value: el.dataset.value }));
    }

    return Array.from(
      selector.querySelectorAll('input.js-option')
    ).map((el) => ({ el, value: el.value }));
  }

  // ─── Rule 1: Color availability across all Style Numbers ────────────────────

  /**
   * For every Color option, remove is-unavailable if ANY Style Number has an
   * available variant with that Color.
   *
   * This runs on load and after every variant change so the base theme's own
   * availability pass (which scopes to the current style number only) never
   * gets to permanently hide a color that exists elsewhere.
   *
   * @param {HTMLElement} pickerEl
   */
  function updateColorAvailability(pickerEl) {
    const allVariants = getAllVariants(pickerEl);
    if (!allVariants.length) return;

    const colorOptions = getColorOptions(pickerEl);
    if (!colorOptions.length) return;

    const colorSelector = pickerEl.optionSelectors[1];
    const isDropdown    = colorSelector.dataset.selectorType === 'dropdown';

    colorOptions.forEach(({ el, value: colorValue }) => {
      const availableInAnyStyle = allVariants.some(
        (v) => v.available && v.options[1] === colorValue
      );

      el.classList.toggle('is-unavailable', !availableInAnyStyle);

      // Also mark the <label> for radio-style selectors
      if (!isDropdown && el.id) {
        const label = colorSelector.querySelector(`label[for="${CSS.escape(el.id)}"]`);
        if (label) label.classList.toggle('is-unavailable', !availableInAnyStyle);
      }
    });
  }

  // ─── Rule 2: Auto-switch Style Number when Color is clicked ─────────────────

  /**
   * Called when a Color option changes. Checks if the current Style Number
   * has an available variant for the newly selected Color. If not, switches
   * to the first Style Number that does.
   *
   * @param {HTMLElement} pickerEl
   * @param {string}      selectedColor  - The newly selected Color value.
   */
  function syncStyleNumberToColor(pickerEl, selectedColor) {
    const allVariants  = getAllVariants(pickerEl);
    const currentStyle = getSelectedValueAt(pickerEl, 0);
    const selectedSize = getSelectedValueAt(pickerEl, 2);

    const hasColor = (styleValue, matchSize) => allVariants.some((v) => {
      if (!v.available)                return false;
      if (v.options[0] !== styleValue) return false;
      if (v.options[1] !== selectedColor) return false;
      if (matchSize && selectedSize !== null && v.options[2] !== selectedSize) return false;
      return true;
    });

    // Current style already carries this Color in *some* size — leave it alone
    // and let the base theme's own size-availability/pruning handle the size.
    if (hasColor(currentStyle, false)) return;

    const styleOptions  = getStyleNumberOptions(pickerEl);
    const styleSelector = pickerEl.optionSelectors[0];
    const isDropdown    = styleSelector.dataset.selectorType === 'dropdown';

    // Prefer a style that also keeps the same Size; fall back to any style
    // that carries the Color at all.
    const targetStyle =
      styleOptions.find(({ value }) => hasColor(value, true)) ||
      styleOptions.find(({ value }) => hasColor(value, false));

    if (!targetStyle) return; // truly no style has this color — nothing to do

    if (isDropdown) {
      const customSelect = targetStyle.el.closest('custom-select');
      if (customSelect && typeof customSelect.selectOption === 'function') {
        customSelect.selectOption(targetStyle.el);
      }
    } else if (!targetStyle.el.checked) {
      targetStyle.el.checked = true;
      targetStyle.el.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // ─── Change handler ──────────────────────────────────────────────────────────

  /**
   * Listens to all change events on the picker.
   * - Color change (index 1): run color availability pass + auto-switch style
   * - Style Number change (index 0): re-run color availability pass
   * - Size change (index 2): re-run color availability pass
   *
   * @param {Event} evt
   */
  function handleChange(evt) {
    const changed = evt.target.closest('.option-selector');
    if (!changed) return;

    const selectors    = Array.from(this.optionSelectors);
    const changedIndex = selectors.indexOf(changed);

    if (changedIndex === 1) {
      // Color changed — rAF lets base theme finish its own DOM updates first,
      // then we restore any colors it wrongly marked unavailable, and
      // auto-switch the Style Number if needed
      const selectedColor = getSelectedValueAt(this, 1);
      requestAnimationFrame(() => {
        updateColorAvailability(this);
        if (selectedColor) syncStyleNumberToColor(this, selectedColor);
      });
    } else if (changedIndex === 0 || changedIndex === 2) {
      // Style Number or Size changed — restore color availability
      requestAnimationFrame(() => updateColorAvailability(this));
    }
  }

  // ─── Initialise ─────────────────────────────────────────────────────────────

  function init() {
    const OriginalVariantPicker = customElements.get('variant-picker');
    if (!OriginalVariantPicker) {
      requestAnimationFrame(init);
      return;
    }

    const proto = OriginalVariantPicker.prototype;

    // Patch addListeners
    const _origAddListeners = proto.addListeners;
    proto.addListeners = function patchedAddListeners() {
      _origAddListeners.call(this);
      if (!this._styleAvailBound) {
        this._styleAvailBound = handleChange.bind(this);
        this.addEventListener('change', this._styleAvailBound);
      }
    };

    // Patch removeListeners
    const _origRemoveListeners = proto.removeListeners;
    proto.removeListeners = function patchedRemoveListeners() {
      _origRemoveListeners.call(this);
      if (this._styleAvailBound) {
        this.removeEventListener('change', this._styleAvailBound);
        this._styleAvailBound = null;
      }
    };

    // Patch updateVariantContent — catches post-fetch DOM refreshes.
    // The base theme re-renders the picker and re-applies is-unavailable scoped
    // to the current style number alone. We always correct that immediately,
    // including on freshly replaced picker instances that never went through
    // the bootstrap loop below.
    const _origUpdateVariantContent = proto.updateVariantContent;
    proto.updateVariantContent = function patchedUpdateVariantContent() {
      _origUpdateVariantContent.call(this);
      updateColorAvailability(this);
    };

    // Bootstrap all already-constructed pickers
    // (constructor fired before this script ran, so we handle them explicitly)
    document.querySelectorAll('variant-picker').forEach((picker) => {
      // Wire change listener missed by the constructor
      if (!picker._styleAvailBound) {
        picker._styleAvailBound = handleChange.bind(picker);
        picker.addEventListener('change', picker._styleAvailBound);
      }

      // Initial color availability pass
      updateColorAvailability(picker);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();